import { O } from 'ts-toolbelt';
import {
    ColumnDefinitionEnum as BaseColumnDefinitionEnum,
    PillUnit,
    CadFeatureMap,
    ColumnDefinitionEnumType,
    CadNextNotificationTypeEnumType,
    CadNextNotificationTypeEnum,
} from '@mark43/cad-api';
import { EventQueueAssignedUnitsCell } from '../event-queues/components/cells/assigned-units-cell';
import { PriorityCell } from './components/cells/priority-cell';
import { NarrativeCell } from './components/cells/narrative-cell';
import {
    BoloQueueRowData,
    EventQueueRowData,
    HospitalQueueRowData,
    ScheduledEventQueueRowData,
    UnitQueueRowData,
} from '../socket/stream-types';
import { BoloPriorityCell } from '../bolos/bolo-queues/components/cells/bolo-priority-cell';
import { StandingMinutesCell } from '../event-queues/components/cells/standing-minutes-cell';
import { WorkingMinutesCell } from '../event-queues/components/cells/working-minutes-cell';
import { TimeTransmittedCell } from '../event-queues/components/cells/time-transmitted-cell';
import { TimeDispatchedCell } from '../event-queues/components/cells/time-dispatched-cell';
import { TimeArrivedCell } from '../event-queues/components/cells/time-arrived-cell';
import { TimeReceivedCell } from './components/cells/time-received-cell';
import { EventLabelsCell } from './components/cells/event-labels-cell';
import { EventLocationCell } from './components/cells/event-location-cell';
import { FunctionComponent } from 'react';
import { stripSearchString } from './utils/strip-search-string';
import { RawQueueRowData } from './store/types-2';
import { TargetOnSceneTimeCell } from '../scheduled-event-queues/components/cells/target-on-scene-time-cell';
import { MinutesInStatusCell } from '../unit-queues/components/cells/minutes-in-status-cell';
import { TimeSinceUnitLocationUpdatedCell } from '../unit-queues/components/cells/time-since-unit-location-updated-cell';
import { UnitClassIconCell } from '../unit-queues/components/cells/unit-class-icon-cell';
import { CallSignColorCodedCell } from '../unit-queues/components/cells/call-sign-color-coded-cell';
import { StatusColorCodedCell } from '../unit-queues/components/cells/status-color-coded-cell';
import { StatusCodeColorCodedCell } from '../unit-queues/components/cells/status-code-color-coded-cell';
import { getInverseDateSortValue } from './utils/get-inverse-date-sort-value';
import { formatHoursMinutes, formatMonthDayYearHoursMinutes } from '../common/helpers/utils';
import {
    getEventQueueRowData,
    getEventQueueRowDataOrScheduledEventQueueRowData,
} from './utils/get-event-queue-row-data';
import { getFormattedDate } from './utils/get-formatted-date';
import { ContextMenuKind } from '../context-menus/types';
import { ScheduledTimeCell } from '../scheduled-event-queues/components/cells/scheduled-time-cell';
import { SortType } from '../common/sorting/types';
import { mergeWith } from 'lodash';
import { ReportingPartyCell } from './components/cells/reporting-party-cell';
import { ReportingPartyPhoneNumberCell } from './components/cells/reporting-party-phone-number-cell';
import { HospitalQueueUnitsCell } from '../hospital-queues/components/cells/hospital-queue-units-cell';
import { HospitalQueueLocationCell } from '../hospital-queues/components/cells/hospital-queue-location-cell';
import { HospitalQueueStatusCell } from '../hospital-queues/components/cells/hosptial-status-cell';
import { BoloLabelsCell } from '../bolos/bolo-queues/components/cells/bolo-labels-cell';

export type ConcreteColumnDefinitionData = typeof columnDefinitionEnum[ColumnDefinitionEnumType];
export type ColumnDefinitionData<
    T extends ColumnDefinitionEnumType = ColumnDefinitionEnumType
> = BaseColumnDefinitionEnumT[T] &
    ColumnDefinitionClientExtension<T, GetCadNextNotificationTypesEnumType<T>>;

/**
 * All client-only properties for columm definitions
 */
type ColumnDefinitionClientExtension<
    T extends ColumnDefinitionEnumType,
    R extends readonly CadNextNotificationTypeEnumType[] | never
> = {
    contentAlignment?: 'LEFT' | 'CENTER' | 'RIGHT';
    getSortValue: (data: RawQueueRowData) => number | string | undefined;
    getFlatValue: (data: RawQueueRowData) => string | undefined;
    getTip?: (data: RawQueueRowData) => string | undefined;
    component?: FunctionComponent<{
        data: RawQueueRowData;
        columnDefinition: ColumnDefinitionData<T>;
    }>;
    getIsEnabled?: (enabledFeatures: CadFeatureMap) => boolean;
    sortType?: SortType;
    sortable?: boolean;
    contextMenuKind?: Extract<ContextMenuKind, 'EVENT'>;
} & ([R] extends [never] ? { eventNotificationNames?: undefined } : { eventNotificationNames: R });

type WidenPrimitive<T> = T extends string
    ? string
    : T extends boolean
    ? boolean
    : T extends number
    ? number
    : T;

type WidenPrimitiveObjectKeys<T> = {
    [P in keyof T]: T[P] extends Record<string, unknown>
        ? WidenPrimitiveObjectKeys<T[P]>
        : WidenPrimitive<T[P]>;
};

type TupleToArray<T> = T extends ReadonlyArray<unknown> | unknown[] ? NonNullable<T[number]>[] : T;
type TupleValuesToArray<T extends Record<string, unknown>> = {
    [P in keyof T]: TupleToArray<T[P]>;
};

type DeepTupleValuesToArray<T extends Record<string, unknown>> = {
    [P in keyof T]: T[P] extends Record<string, unknown>
        ? DeepTupleValuesToArray<T[P]>
        : TupleToArray<T[P]>;
};

type OverwritePropertiesIfDefined<T, T2 extends Partial<WidenPrimitiveObjectKeys<T>>> = {
    [P in keyof T]: T2[P] extends string | number | string[] | number[] ? T2[P] : T[P];
};

type OverWriteAll<
    T1 extends Record<string, Record<string, unknown>>,
    T2 extends O.Partial<WidenPrimitiveObjectKeys<T1>, 'deep'>
> = {
    [P in keyof T1]: T2[P] extends Partial<WidenPrimitiveObjectKeys<T1[P]>>
        ? OverwritePropertiesIfDefined<T1[P], T2[P]>
        : T1[P];
};

type BaseColumnDefinitionEnumT = typeof BaseColumnDefinitionEnum;
type ExtendedColumnDefinitionEnum = {
    [P in keyof BaseColumnDefinitionEnumT]: BaseColumnDefinitionEnumT[P] & ColumnDefinitionData<P>;
};

type OverridableProperties = O.Partial<
    WidenPrimitiveObjectKeys<
        {
            [P in keyof BaseColumnDefinitionEnumT]: TupleValuesToArray<
                O.Partial<Omit<BaseColumnDefinitionEnumT[P], 'name' | 'value'>>
            >;
        }
    >
>;

/**
 * Event notification mapping used as source of truth both for type checking and runtime type guards
 */
const columnDefinitionNameToNotificationTypes = {
    BEAT: ['BEAT'],
    PRIORITY: ['EVENT_PRIORITY'],
    EVENT_NUMBER: ['NEW_EVENT'],
    EVENT_NUMBER_LAST_4_DIGITS: ['NEW_EVENT'],
    EVENT_LABELS: ['EVENT_LABELS'],
    CFS_NUMBER_AND_TEXT: ['CFS'],
    CFS_NUMBER: ['CFS'],
    CFS_TEXT: ['CFS'],
    RISK_LEVEL: ['RISK_LEVEL'],
    EVENT_LOCATION: ['EVENT_LOCATION'],
    EVENT_LOCATION_NOTES: ['EVENT_LOCATION_NOTES'],
    EVENT_RADIO_CHANNEL: ['RADIO_CHANNEL'],
    DISPATCH_AREA: ['DISPATCH_AREA'],
    CROSS_STREETS: ['CROSS_STREETS'],
    REPORTING_PARTY: ['REPORTING_PARTY_FIRST_NAME', 'REPORTING_PARTY_LAST_NAME'],
    REPORTING_PARTY_PHONE_NUMBER: ['REPORTING_PARTY_PHONE_NUMBER'],
    AGENCY: ['AGENCY'],
    ALARM_LEVEL: ['ALARM_LEVEL'],
} as const;

type ColumnDefinitionNameToNotificationTypes = typeof columnDefinitionNameToNotificationTypes;

type GetCadNextNotificationTypesEnumType<
    T
> = T extends keyof ColumnDefinitionNameToNotificationTypes
    ? ColumnDefinitionNameToNotificationTypes[T]
    : never;

type ClientExtensionInput = {
    [P in keyof ExtendedColumnDefinitionEnum]: ColumnDefinitionClientExtension<
        ExtendedColumnDefinitionEnum[P]['name'],
        GetCadNextNotificationTypesEnumType<ExtendedColumnDefinitionEnum[P]['name']>
    >;
};

function mergeColumnDefinition<T extends OverridableProperties, R extends ClientExtensionInput>(
    feOverrides: T,
    feExtensions: R
): OverWriteAll<DeepTupleValuesToArray<BaseColumnDefinitionEnumT>, T> &
    OverWriteAll<ClientExtensionInput, R> {
    return mergeWith(BaseColumnDefinitionEnum, feOverrides, feExtensions, (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
            return srcValue;
        }
    });
}

const stubFunction = (_: unknown): string => 'STUB';

const stubExtension = {
    getIsEnabled: () => false,
    getFlatValue: stubFunction,
    getSortValue: stubFunction,
    validEntityTypes: [],
};

/**
 * Matches server enum {@link cobalt-cad-client/cad-client/src/main/java/com/mark43/cad/model/queues/ColumnDefinition.java}
 * keys but extends each entry with additional fields.
 */
const columnDefinitionEnum = mergeColumnDefinition(
    {
        // omit SCHEDULED_AGENCY_EVENT, which needs to stay on BE enum to support legacy CAD
        TIME_RECEIVED: { validEntityTypes: ['CAD_AGENCY_EVENT', 'UNIT'] },
    },
    {
        ADDITIONAL_CFS: {
            getIsEnabled: (enabledFeatures) => enabledFeatures.additionalCallForService,
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.additionalCfs,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.additionalCfs,
            getTip: (data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.additionalCfs,
        },
        DISTANCE_FROM_EVENT: {
            getFlatValue: (data: EventQueueRowData) => String(data?.distanceFromEvent),
            getSortValue: (data: EventQueueRowData) => data?.distanceFromEvent,
            validEntityTypes: [],
        },
        PRIORITY: {
            eventNotificationNames: ['EVENT_PRIORITY'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.priority,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.priority,
            component: PriorityCell,
        },
        EVENT_NUMBER: {
            eventNotificationNames: ['NEW_EVENT'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumber,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumber,
            getTip: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumber,
            contextMenuKind: 'EVENT',
        },
        EVENT_NUMBER_LAST_4_DIGITS: {
            eventNotificationNames: ['NEW_EVENT'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumberLast4Digits,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumberLast4Digits,
            getTip: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.eventNumber,
            contextMenuKind: 'EVENT',
        },
        EVENT_LABELS: {
            eventNotificationNames: ['EVENT_LABELS'],
            component: EventLabelsCell,
            getIsEnabled: (enabledFeatures) => enabledFeatures.eventLabels,
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => {
                const dataToUse = getEventQueueRowDataOrScheduledEventQueueRowData(data);
                if (!dataToUse) {
                    return '';
                }
                return dataToUse.eventLabels
                    ? dataToUse.eventLabels?.map((eventLabel) => eventLabel.name).join(' ')
                    : '';
            },
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => {
                const dataToUse = getEventQueueRowDataOrScheduledEventQueueRowData(data);
                if (!dataToUse) {
                    return '';
                }
                return dataToUse.eventLabels
                    ? dataToUse.eventLabels?.map((eventLabel) => eventLabel.name).join(' ')
                    : '';
            },
        },
        EVENT_STATUS: {
            getFlatValue: (data: EventQueueRowData) => data.eventStatus,
            getSortValue: (data: EventQueueRowData) => data.eventStatus,
        },
        CFS_NUMBER_AND_TEXT: {
            eventNotificationNames: ['CFS'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumberAndText,
            getSortValue: (data: EventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumberAndText,
            getTip: (data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumberAndText,
        },
        CFS_NUMBER: {
            eventNotificationNames: ['CFS'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumber,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumber,
            getTip: (data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.cfsNumberAndText,
        },
        CFS_TEXT: {
            eventNotificationNames: ['CFS'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowData(data)?.cfsText,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowData(data)?.cfsText,
        },
        RISK_LEVEL: {
            contentAlignment: 'CENTER',
            eventNotificationNames: ['RISK_LEVEL'],
            getIsEnabled: (enabledFeatures) => enabledFeatures.eventsHaveRiskLevel,
            getFlatValue: (data: EventQueueRowData) => data.riskLevel,
            getSortValue: (data: EventQueueRowData) => data.riskLevel,
        },
        EVENT_LOCATION: {
            component: EventLocationCell,
            eventNotificationNames: ['EVENT_LOCATION'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventLocation,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventLocation,
        },
        EVENT_LOCATION_NOTES: {
            eventNotificationNames: ['EVENT_LOCATION_NOTES'],
            getIsEnabled: (enabledFeatures) => enabledFeatures.eventLocationNotes,
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventLocationNotes,
            getSortValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventLocationNotes,
            getTip: (data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventLocationTip,
        },
        EVENT_RADIO_CHANNEL: {
            eventNotificationNames: ['RADIO_CHANNEL'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventRadioChannel,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventRadioChannel,
            getTip: (data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventRadioChannel,
            getIsEnabled: (enabledFeatures) => enabledFeatures.radioChannels,
        },
        DISPATCH_AREA: {
            contentAlignment: 'CENTER',
            eventNotificationNames: ['DISPATCH_AREA'],
            getFlatValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowData(data)?.dispatchArea,
            getSortValue: (
                data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData
            ) => getEventQueueRowData(data)?.dispatchArea,
            contextMenuKind: 'EVENT',
        },
        CROSS_STREETS: {
            eventNotificationNames: ['CROSS_STREETS'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.crossStreets,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.crossStreets,
            contextMenuKind: 'EVENT',
        },
        NARRATIVE: {
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) =>
                stripSearchString(
                    getEventQueueRowDataOrScheduledEventQueueRowData(data)?.narrative ?? ''
                ),
            getSortValue: (data: EventQueueRowData | ScheduledEventQueueRowData) =>
                stripSearchString(
                    getEventQueueRowDataOrScheduledEventQueueRowData(data)?.narrative ?? ''
                ),
            component: NarrativeCell,
        },
        ASSIGNED_STATUS: {
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.queueFiltersMetadata?.isAssigned
                    ? 'Dispatched'
                    : 'Pending',
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.queueFiltersMetadata?.isAssigned
                    ? 'Dispatched'
                    : 'Pending',
        },
        ASSIGNED_UNITS: {
            component: EventQueueAssignedUnitsCell,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)
                    ?.assignedUnits?.map((unit: PillUnit) => unit.callSign)
                    .join(' '),
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)
                    ?.assignedUnits?.map((unit: PillUnit) => unit.callSign)
                    .join(' '),
            sortType: 'alphabetical',
        },
        TIME_RECEIVED: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeReceived,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeReceived,
            component: TimeReceivedCell,
        },
        TIME_TRANSMITTED: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeTransmitted,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeTransmitted,
            component: TimeTransmittedCell,
        },
        TIME_DISPATCHED: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeDispatched,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeDispatched,
            component: TimeDispatchedCell,
        },
        TIME_ARRIVED: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.timeArrived,
            getSortValue: (data: EventQueueRowData) => getEventQueueRowData(data)?.timeArrived,
            component: TimeArrivedCell,
        },
        MINUTES_STANDING: {
            component: StandingMinutesCell,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getInverseDateSortValue(getEventQueueRowData(data)?.timeTransmitted),
            getFlatValue: () => 'todo shouldnt need this',
        },
        MINUTES_WORKING: {
            component: WorkingMinutesCell,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getInverseDateSortValue(getEventQueueRowData(data)?.timeDispatched),
            getFlatValue: () => 'todo shouldnt need this',
        },
        DISPATCHER: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.dispatcher,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.dispatcher,
        },
        CALL_TAKER: {
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.callTaker,
            getSortValue: (data: EventQueueRowData | ScheduledEventQueueRowData) =>
                getEventQueueRowDataOrScheduledEventQueueRowData(data)?.callTaker,
        },
        REPORTING_PARTY: {
            component: ReportingPartyCell,
            eventNotificationNames: ['REPORTING_PARTY_FIRST_NAME', 'REPORTING_PARTY_LAST_NAME'],
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.reportingParty,
            getSortValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.reportingParty,
        },
        REPORTING_PARTY_PHONE_NUMBER: {
            component: ReportingPartyPhoneNumberCell,
            eventNotificationNames: ['REPORTING_PARTY_PHONE_NUMBER'],
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.reportingPartyPhoneNumber,
            getSortValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.reportingPartyPhoneNumber,
        },
        CLASS: {
            contentAlignment: 'LEFT',
            getFlatValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventClass,
            getSortValue: (
                data: EventQueueRowData | ScheduledEventQueueRowData | UnitQueueRowData
            ) => getEventQueueRowDataOrScheduledEventQueueRowData(data)?.eventClass,
        },
        // unit columns
        UNIT_DISPATCH_AREA: {
            getFlatValue: (data: UnitQueueRowData) => data.unitDispatchArea,
            getSortValue: (data: UnitQueueRowData) => data.unitDispatchArea,
        },
        CALL_SIGN: {
            contentAlignment: 'CENTER',
            getFlatValue: (data: UnitQueueRowData) => data.callSign,
            getSortValue: (data: UnitQueueRowData) => data.callSign,
            getTip: (data: UnitQueueRowData) => data.personnelFullName,
            sortType: 'alphabetical',
        },
        CALL_SIGN_COLOR_CODED: {
            getFlatValue: (data: UnitQueueRowData) => data.callSign,
            getSortValue: (data: UnitQueueRowData) => data.callSign,
            component: CallSignColorCodedCell,
            sortType: 'alphabetical',
        },
        AGENCY: {
            eventNotificationNames: ['AGENCY'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.agency,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.agency,
        },
        AVAILABILITY: {
            getFlatValue: (data: UnitQueueRowData) => data.availability,
            getSortValue: (data: UnitQueueRowData) => data.availability,
        },
        STATUS: {
            getFlatValue: (data: UnitQueueRowData) => data.status,
            getSortValue: (data: UnitQueueRowData) => data.status,
        },
        STATUS_COLOR_CODED: {
            getFlatValue: (data: UnitQueueRowData) => data.status,
            getSortValue: (data: UnitQueueRowData) => data.status,
            component: StatusColorCodedCell,
        },
        STATUS_CODE: {
            contentAlignment: 'CENTER',
            getFlatValue: (data: UnitQueueRowData) => data.statusCode,
            getSortValue: (data: UnitQueueRowData) => data.statusCode,
        },
        STATUS_CODE_COLOR_CODED: {
            getFlatValue: (data: UnitQueueRowData) => data.statusCode,
            getSortValue: (data: UnitQueueRowData) => data.statusCode,
            component: StatusCodeColorCodedCell,
        },
        MINUTES_IN_STATUS: {
            getFlatValue: (data: UnitQueueRowData) => data.timeInStatusDateTime,
            getSortValue: (data: UnitQueueRowData) =>
                getInverseDateSortValue(data.timeInStatusDateTime),
            component: MinutesInStatusCell,
        },
        UNIT_TYPE: {
            getFlatValue: (data: UnitQueueRowData) => data.primaryUnitType,
            getSortValue: (data: UnitQueueRowData) => data.primaryUnitType,
        },
        UNIT_IS_ASSISTING: {
            getFlatValue: (data: UnitQueueRowData) => data.unitIsAssisting,
            getSortValue: (data: UnitQueueRowData) => data.unitIsAssisting,
            sortType: 'alphabetical',
        },
        PATROL_TYPE: {
            getFlatValue: (data: UnitQueueRowData) => data.patrolType,
            getSortValue: (data: UnitQueueRowData) => data.patrolType,
        },
        PATROL_ADMIN_NOTES: {
            getFlatValue: (data: UnitQueueRowData) => data.statusNotes,
            getSortValue: (data: UnitQueueRowData) => data.statusNotes,
            getTip: (data: UnitQueueRowData) => data.statusNotes,
        },
        STATUS_LOCATION: {
            getFlatValue: (data: UnitQueueRowData) => data.statusLocation,
            getSortValue: (data: UnitQueueRowData) => data.statusLocation,
            getTip: (data: UnitQueueRowData) => data.statusLocationTip,
        },
        UNIT_LOCATION: {
            getFlatValue: (data: UnitQueueRowData) => data.unitLocation,
            getSortValue: (data: UnitQueueRowData) => data.unitLocation,
            getTip: (data: UnitQueueRowData) => data.unitLocationTip,
        },
        UNIT_LOCATION_NOTES: {
            getFlatValue: (data: UnitQueueRowData) => data.unitLocationNotes,
            getSortValue: (data: UnitQueueRowData) => data.unitLocationNotes,
            getTip: (data: UnitQueueRowData) => data.unitLocationNotes,
        },
        TIME_SINCE_UNIT_LOCATION_UPDATED: {
            getFlatValue: (data: UnitQueueRowData) => data.unitLocationInfoCreatedDateTime,
            getSortValue: (data: UnitQueueRowData) =>
                getInverseDateSortValue(data.unitLocationInfoCreatedDateTime),
            component: TimeSinceUnitLocationUpdatedCell,
        },
        PERSONNEL_FULL_NAME: {
            getFlatValue: (data: UnitQueueRowData) => data.personnelFullName,
            getSortValue: (data: UnitQueueRowData) => data.personnelFullName,
            getTip: (data: UnitQueueRowData) => data.personnelFullName,
        },
        PERSONNEL_ABBREVIATED_NAME: {
            getFlatValue: (data: UnitQueueRowData) => data.personnelAbbreviatedName,
            getSortValue: (data: UnitQueueRowData) => data.personnelAbbreviatedName,
            getTip: (data: UnitQueueRowData) => data.personnelFullName,
        },
        TAG_NUMBER: {
            getFlatValue: (data: UnitQueueRowData) => data.tagNumber,
            getSortValue: (data: UnitQueueRowData) => data.tagNumber,
        },
        PRIMARY_UNIT_TYPE_CODE: {
            getFlatValue: (data: UnitQueueRowData) => data.primaryUnitTypeCode,
            getSortValue: (data: UnitQueueRowData) => data.primaryUnitTypeCode,
        },
        ALL_UNIT_TYPES_CODE: {
            getFlatValue: (data: UnitQueueRowData) => data.allUnitTypesCode,
            getSortValue: (data: UnitQueueRowData) => data.allUnitTypesCode,
        },
        ALL_UNIT_TYPES_TEXT: {
            getFlatValue: (data: UnitQueueRowData) => data.allUnitTypes,
            getSortValue: (data: UnitQueueRowData) => data.allUnitTypes,
        },
        UNIT_CLASS_ICON: {
            getFlatValue: (data: UnitQueueRowData) => data.unitClass,
            getSortValue: (data: UnitQueueRowData) => data.unitClass,
            component: UnitClassIconCell,
        },
        UNIT_CLASS_TEXT: {
            getFlatValue: (data: UnitQueueRowData) => data.unitClass,
            getSortValue: (data: UnitQueueRowData) => data.unitClass,
        },
        EQUIPMENT: {
            getFlatValue: (data: UnitQueueRowData) => data.equipment,
            getSortValue: (data: UnitQueueRowData) => data.equipment,
        },
        SKILLS: {
            getFlatValue: (data: UnitQueueRowData) => data.skills,
            getSortValue: (data: UnitQueueRowData) => data.skills,
            getTip: (data: UnitQueueRowData) => data.skillsTip,
        },
        NOTES: {
            getFlatValue: (data: UnitQueueRowData) => data.shiftNotes,
            getSortValue: (data: UnitQueueRowData) => data.shiftNotes,
        },
        SHIFT: {
            getFlatValue: (data: UnitQueueRowData) =>
                data.shiftStartUtc && data.shiftEndUtc
                    ? `${formatHoursMinutes(data.shiftStartUtc)} - ${formatHoursMinutes(
                          data.shiftEndUtc
                      )}`
                    : '',
            getSortValue: (data: UnitQueueRowData) =>
                data.shiftStartUtc && data.shiftEndUtc
                    ? `${formatHoursMinutes(data.shiftStartUtc)} - ${formatHoursMinutes(
                          data.shiftEndUtc
                      )}`
                    : '',
        },
        STATION: {
            getFlatValue: (data: UnitQueueRowData) => data.station,
            getSortValue: (data: UnitQueueRowData) => data.station,
        },
        BEAT: {
            contentAlignment: 'CENTER',
            eventNotificationNames: ['BEAT'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.beat,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.beat,
        },
        SCHEDULED_TIME: {
            contentAlignment: 'LEFT',
            getIsEnabled: (enabledFeatures) => !enabledFeatures.improvedScheduledEvents,
            getFlatValue: (data: ScheduledEventQueueRowData) => data.targetOnSceneTime,
            getSortValue: (data: ScheduledEventQueueRowData) => data.targetOnSceneTime,
            component: ScheduledTimeCell,
        },
        SCHEDULED_RECURRING: {
            getFlatValue: (data: ScheduledEventQueueRowData) => data.scheduledRecurring,
            getSortValue: (data: ScheduledEventQueueRowData) => data.scheduledRecurring,
            getTip: (data: ScheduledEventQueueRowData) =>
                `${data.recurringTipWithoutDate}${
                    data.recurringEndDateUtc ? getFormattedDate(data.recurringEndDateUtc) : ''
                }`,
        },
        SCHEDULED_EVENT_NUMBER: {
            getFlatValue: (data: ScheduledEventQueueRowData) => data.scheduledEventNumber,
            getSortValue: (data: ScheduledEventQueueRowData) => data.scheduledEventNumber,
        },
        SCHEDULED_EVENT_NUMBER_LAST_4_DIGITS: {
            getFlatValue: (data: ScheduledEventQueueRowData) =>
                data.scheduledEventNumberLast4Digits,
            getSortValue: (data: ScheduledEventQueueRowData) =>
                data.scheduledEventNumberLast4Digits,
            getTip: (data: ScheduledEventQueueRowData) => data.scheduledEventNumber,
        },
        PERSONNEL_BADGE_NUMBER: {
            getFlatValue: (data: UnitQueueRowData) => data.personnelBadgeNumber,
            getSortValue: (data: UnitQueueRowData) => data.personnelBadgeNumber,
            getTip: (data: UnitQueueRowData) => data.personnelBadgeNumber,
        },
        TARGET_ON_SCENE_TIME: {
            contentAlignment: 'LEFT',
            getIsEnabled: (enabledFeatures) => enabledFeatures.improvedScheduledEvents,
            getFlatValue: (data: ScheduledEventQueueRowData) => data.targetOnSceneTime,
            getSortValue: (data: ScheduledEventQueueRowData) => data.targetOnSceneTime,
            component: TargetOnSceneTimeCell,
        },
        DISPLAY_IN_QUEUE: {
            getIsEnabled: (enabledFeatures) => enabledFeatures.improvedScheduledEvents,
            getFlatValue: (data: ScheduledEventQueueRowData) => data.activationTime,
            getSortValue: (data: ScheduledEventQueueRowData) => data.activationTime,
        },
        ALARM_LEVEL: {
            eventNotificationNames: ['ALARM_LEVEL'],
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.alarmLevel,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.alarmLevel,
        },
        UNIT_AGENCY: {
            getFlatValue: (data: UnitQueueRowData) => data.unitAgency,
            getSortValue: (data: UnitQueueRowData) => data.unitAgency,
        },
        UNIT_BEAT: {
            contentAlignment: 'CENTER',
            getFlatValue: (data: UnitQueueRowData) => data.unitBeat,
            getSortValue: (data: UnitQueueRowData) => data.unitBeat,
        },
        UNIT_STAFF_COUNT: {
            getFlatValue: (data: UnitQueueRowData) => data.staffCount,
            getSortValue: (data: UnitQueueRowData) => data.staffCount,
        },
        EVENT_STAFF_COUNT: {
            getFlatValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.staffCount,
            getSortValue: (data: EventQueueRowData | UnitQueueRowData) =>
                getEventQueueRowData(data)?.staffCount,
        },
        BOLO_NUMBER: {
            getFlatValue: (data: BoloQueueRowData) => data.boloNumber,
            getSortValue: (data: BoloQueueRowData) => data.boloNumber,
        },
        BOLO_DESCRIPTION: stubExtension,
        BOLO_PRIORITY: {
            getFlatValue: (data: BoloQueueRowData) => data.priority,
            getSortValue: (data: BoloQueueRowData) => data.priority,
            component: BoloPriorityCell,
        },
        BOLO_CATEGORY: stubExtension,
        BOLO_STATUS: stubExtension,
        BOLO_ACTIVE_DATE: {
            getFlatValue: (data: BoloQueueRowData) =>
                data.activeDateUtc ? formatMonthDayYearHoursMinutes(data.activeDateUtc) : undefined,
            getSortValue: (data: BoloQueueRowData) => data.activeDateUtc,
        },
        BOLO_EXPIRY_DATE: stubExtension,
        BOLO_ORIGINATING_AGENCY: stubExtension,
        BOLO_DISPATCH_AREAS: {
            getFlatValue: (data: BoloQueueRowData) =>
                data.dispatchAreas?.map((da) => da.label).join(', '),
            getSortValue: (data: BoloQueueRowData) =>
                data.dispatchAreas?.map((da) => da.label).join(', '),
        },
        BOLO_LOCATION: {
            getFlatValue: (data: BoloQueueRowData) => data.location,
            getSortValue: (data: BoloQueueRowData) => data.location,
            getTip: (data: BoloQueueRowData) => data.locationTip,
        },
        BOLO_LOCATION_DETAILS: stubExtension,
        BOLO_RELATED_CAD_EVENT_NUMBER: stubExtension,
        BOLO_LABELS: {
            getFlatValue: (data: BoloQueueRowData) =>
                data.labels?.map((label) => label.label).join(', '),
            getSortValue: (data: BoloQueueRowData) =>
                data.labels?.map((label) => label.label).join(', '),
            component: BoloLabelsCell,
        },
        BOLO_LAST_UPDATED: {
            getFlatValue: (data: BoloQueueRowData) =>
                data.lastUpdatedDateUtc
                    ? formatMonthDayYearHoursMinutes(data.lastUpdatedDateUtc)
                    : undefined,
            getSortValue: (data: BoloQueueRowData) => data.lastUpdatedDateUtc,
        },
        BOLO_PRIMARY_SUBJECT: {
            getFlatValue: (data: BoloQueueRowData) => data.primarySubjectDisplay,
            getSortValue: (data: BoloQueueRowData) => data.primarySubjectDisplay,
        },
        BOLO_SUBDIVISIONS: {
            getFlatValue: (data: BoloQueueRowData) =>
                data.subdivisions?.map((subdivision) => subdivision.label).join(', '),
            getSortValue: (data: BoloQueueRowData) =>
                data.subdivisions?.map((subdivision) => subdivision.label).join(', '),
        },
        BOLO_TYPE: {
            getFlatValue: (data: BoloQueueRowData) => data.boloType?.label,
            getSortValue: (data: BoloQueueRowData) => data.boloType?.label,
        },
        HOSPITAL_NAME: {
            getFlatValue: (data: HospitalQueueRowData) => data.name,
            getSortValue: (data: HospitalQueueRowData) => data.name,
        },
        HOSPITAL_CAPABILITIES: {
            getSortValue: (data: HospitalQueueRowData) =>
                data?.hospitalCapabilities?.sort().join(', '),
            getFlatValue: (data: HospitalQueueRowData) => data?.hospitalCapabilities?.join(', '),
            sortType: 'alphabetical',
        },
        HOSPITAL_LOCATION: {
            component: HospitalQueueLocationCell,
            getFlatValue: (data: HospitalQueueRowData) => data.location,
            getSortValue: (data: HospitalQueueRowData) => data.location,
        },
        HOSPITAL_NOTES: {
            getFlatValue: (data: HospitalQueueRowData) => data.notes,
            getSortValue: (data: HospitalQueueRowData) => data.notes,
        },
        HOSPITAL_UNITS: {
            component: HospitalQueueUnitsCell,
            getFlatValue: (data: HospitalQueueRowData) =>
                data?.pillUnits?.map((unit: PillUnit) => unit.callSign).join(' ') ?? '',
            getSortValue: (data: HospitalQueueRowData) =>
                data?.pillUnits?.map((unit: PillUnit) => unit.callSign).join(' ') ?? '',
            sortType: 'alphabetical',
        },
        HOSPITAL_STATUS: {
            component: HospitalQueueStatusCell,
            getFlatValue: (data: HospitalQueueRowData) => data.hospitalStatus,
            getSortValue: (data: HospitalQueueRowData) => data.hospitalStatus,
        },
    }
);

export const columnDefinitionNamesByEventNotificationName: Partial<
    Record<CadNextNotificationTypeEnumType, Partial<Record<ColumnDefinitionEnumType, true>>>
> = {};

for (const columnDefinition of Object.values(columnDefinitionEnum)) {
    if ('eventNotificationNames' in columnDefinition) {
        columnDefinition.eventNotificationNames?.forEach((eventNotificationName) => {
            if (isEventNotificationKey(eventNotificationName)) {
                if (!columnDefinitionNamesByEventNotificationName[eventNotificationName]) {
                    columnDefinitionNamesByEventNotificationName[eventNotificationName] = {};
                }
                const innerRecord =
                    columnDefinitionNamesByEventNotificationName[eventNotificationName];
                if (innerRecord) {
                    innerRecord[columnDefinition.name] = true;
                }
            }
        });
    }
}

function isEventNotificationKey(key: string): key is CadNextNotificationTypeEnumType {
    return !!key && key in CadNextNotificationTypeEnum;
}

export default columnDefinitionEnum;

const CFS_GROUP_NAME = '@@CFS_GROUP@@';
const LOCATION_GROUP_NAME = '@@LOCATION_GROUP@@';
const REPORTING_PARTY_GROUP_NAME = '@@REPORTING_PARTY_GROUP@@';

export const columnDefinitionNameToColumnGroup: Partial<Record<string, string>> = {
    // cfs group
    [columnDefinitionEnum.CFS_NUMBER.name]: CFS_GROUP_NAME,
    [columnDefinitionEnum.CFS_TEXT.name]: CFS_GROUP_NAME,
    [columnDefinitionEnum.CFS_NUMBER_AND_TEXT.name]: CFS_GROUP_NAME,
    [columnDefinitionEnum.PRIORITY.name]: CFS_GROUP_NAME,
    [columnDefinitionEnum.RISK_LEVEL.name]: CFS_GROUP_NAME,
    // location group
    [columnDefinitionEnum.EVENT_LOCATION.name]: LOCATION_GROUP_NAME,
    [columnDefinitionEnum.EVENT_LOCATION_NOTES.name]: LOCATION_GROUP_NAME,
    [columnDefinitionEnum.EVENT_RADIO_CHANNEL.name]: LOCATION_GROUP_NAME,
    [columnDefinitionEnum.CROSS_STREETS.name]: LOCATION_GROUP_NAME,
    [columnDefinitionEnum.DISPATCH_AREA.name]: LOCATION_GROUP_NAME,
    [columnDefinitionEnum.BEAT.name]: LOCATION_GROUP_NAME,
    // reporting party group
    [columnDefinitionEnum.REPORTING_PARTY.name]: REPORTING_PARTY_GROUP_NAME,
    [columnDefinitionEnum.REPORTING_PARTY_PHONE_NUMBER.name]: REPORTING_PARTY_GROUP_NAME,
};

export const columnGroupToEventNotificationNames: Record<
    string,
    CadNextNotificationTypeEnumType[]
> = {
    [CFS_GROUP_NAME]: ['CFS', 'EVENT_PRIORITY', 'RISK_LEVEL'],
    [LOCATION_GROUP_NAME]: [
        'EVENT_LOCATION',
        'EVENT_LOCATION_NOTES',
        'BEAT',
        'DISPATCH_AREA',
        'CROSS_STREETS',
        'RADIO_CHANNEL',
        'SUBDIVISION',
    ],
    [REPORTING_PARTY_GROUP_NAME]: [
        'REPORTING_PARTY_FIRST_NAME',
        'REPORTING_PARTY_LAST_NAME',
        'REPORTING_PARTY_PHONE_NUMBER',
    ],
};
